// app/(default)/category/[categoryId]/CategoryProductList.tsx
'use client'; // Wichtig: Dies ist eine Client Komponente

import React, { useState, useTransition } from 'react';
import { UnifiedProductDisplay } from '@/app/api/types'; // Pfad ggf. anpassen
import { UnifiedProductCard } from '@/components/ui/landing/UnifiedProductCard'; // Pfad ggf. anpassen
import { loadMoreCategoryProducts } from '@/app/api/actions/category-actions'; // Importiere die Server Action
import Spinner from "@/components/ui/LoadingSpinner";

interface CategoryProductListProps {
  initialProducts: UnifiedProductDisplay[];
  initialHasMore: boolean;
  totalProducts: number; // Gesamtzahl aller Produkte für diese Kategorie
  categoryId: string;
  urgentOnly: boolean;
  limitPerPage: number; // Wie viele Produkte pro Klick laden
}

export default function CategoryProductList({
                                              initialProducts,
                                              initialHasMore,
                                              totalProducts,
                                              categoryId,
                                              urgentOnly,
                                              limitPerPage,
                                            }: CategoryProductListProps) {
  const [products, setProducts] = useState<UnifiedProductDisplay[]>(initialProducts);
  // Starten auf Seite 2, da Seite 1 initial geladen wurde
  const [currentPage, setCurrentPage] = useState<number>(2);
  const [hasMore, setHasMore] = useState<boolean>(initialHasMore);
  // useTransition für besseres Loading-State-Management mit Server Actions
  const [isPending, startTransition] = useTransition();

  const handleLoadMore = () => {
    // Verhindere mehrfaches Laden
    if (isPending) return;

    startTransition(async () => {
      try {
        const result = await loadMoreCategoryProducts(
          categoryId,
          currentPage,
          limitPerPage,
          urgentOnly
        );

        if (result.items.length > 0) {
          // Füge neue Produkte hinzu (verhindert Duplikate durch Set)
          setProducts((prev) => {
            const existingIds = new Set(prev.map(p => p.id));
            const newItems = result.items.filter(item => !existingIds.has(item.id));
            return [...prev, ...newItems];
          });
          setCurrentPage((prev) => prev + 1); // Gehe zur nächsten Seite
        }
        setHasMore(result.hasMore); // Aktualisiere, ob es noch mehr gibt

      } catch (error) {
        console.error('Fehler beim Laden weiterer Produkte:', error);
        // Optional: Fehlermeldung für den User anzeigen
        setHasMore(false); // Sicherheitshalber annehmen, dass nichts mehr kommt
      }
    });
  };

  return (
    <div>
      {products.length > 0 ? (
        <>
          {/* Das Grid, das die Produkte anzeigt */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {products.map((product, index) => (
              <UnifiedProductCard key={product.id} product={product} index={index} />
            ))}
          </div>

          {/* "Mehr laden"-Button oder Ladezustand */}
          <div className="text-center">
            {hasMore ? (
              <button
                onClick={handleLoadMore}
                disabled={isPending} // Deaktiviert während des Ladens
                className="px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? (
                  <>
                    <Spinner size="sm" />
                    Wird geladen...
                  </>
                ) : (
                  'Weitere Angebote laden'
                )}
              </button>
            ) : (
              // Optional: Nachricht, wenn alles geladen ist
              <div className="flex flex-col items-center gap-6">
                {/* CTA Button für andere Kategorien */}
                <a
                  href="/categories"
                  className="px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-full font-medium transition-colors"
                >
                  Entdecke noch mehr Angebote in anderen Kategorien
                </a>
              </div>
            )}
          </div>
        </>
      ) : (
        // Fallback, falls initial keine Produkte geladen wurden
        <p className="text-center text-gray-500 py-12">
          Aktuell sind keine Angebote in dieser Kategorie verfügbar. Schau bald wieder vorbei!
        </p>
      )}

    </div>
  );
}